import { useState, useEffect } from "react";

/**
 * Custom hook to track countdown to PSA-10's next hourly price update.
 * Unlike the daily indices (POKE, real estate, watches), PSA-10 refreshes on
 * the top of every UTC hour — 1:00, 2:00, 3:00, ... — matching update_system's
 * psa10-hourly-index.js crontab (`0 * * * *`). A 2-minute window after each
 * hour boundary is treated as "updating" (green), the rest of the hour counts
 * down (purple) to the next boundary — same open/counting split the other
 * countdown hooks use, just on an hourly cadence instead of a daily one.
 * @returns {Object} timeRemaining and isMarketOpen state
 */
const UPDATE_WINDOW_MINUTES = 2;

const usePsa10MarketCountdown = () => {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isMarketOpen, setIsMarketOpen] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const utcMinutes = now.getUTCMinutes();
      const utcSeconds = now.getUTCSeconds();

      const updating = utcMinutes < UPDATE_WINDOW_MINUTES;
      setIsMarketOpen(updating);

      let minutesRemaining;
      let secondsRemaining;

      if (updating) {
        // Counting down to the end of the "updating" window itself.
        minutesRemaining = UPDATE_WINDOW_MINUTES - 1 - utcMinutes;
        secondsRemaining = 60 - utcSeconds;
        if (secondsRemaining === 60) secondsRemaining = 0;
      } else {
        // Counting down to the top of the next hour.
        minutesRemaining = 59 - utcMinutes;
        secondsRemaining = 60 - utcSeconds;
        if (secondsRemaining === 60) {
          secondsRemaining = 0;
          minutesRemaining += 1;
        }
      }

      setTimeRemaining({ hours: 0, minutes: minutesRemaining, seconds: secondsRemaining });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, []);

  return { timeRemaining, isMarketOpen };
};

export default usePsa10MarketCountdown;
